// Data-quality pass over the whole model: what a room-book check would flag first.
import type { PropsElement } from '../props';
import { isType, prop } from '../props';
import type { ToolFn, ToolSpec } from '../types';

export const auditSpec: ToolSpec = {
  name: 'audit_model',
  description:
    'Data-quality audit of the whole model: elements without material, without any quantity, without a storey, doors without a fire rating, walls without a load-bearing flag, Revit placeholder property values. Returns counts and ids per issue; use scene(isolate) or highlight on an issue afterwards.',
  input_schema: { type: 'object', properties: { type: { type: 'string' } } },
};

export const audit =
  (elements: PropsElement[]): ToolFn =>
  ({ type }) => {
    const t = String(type ?? '').toUpperCase();
    const pool = t ? elements.filter((e) => e.type.toUpperCase().startsWith(t)) : elements;
    const issues: Record<string, number[]> = {
      no_material: [],
      no_quantity: [],
      no_storey: [],
      door_without_fire_rating: [],
      wall_without_loadbearing_flag: [],
      placeholder_values: [],
    };
    let placeholders = 0;
    for (const e of pool) {
      if (!e.material) issues.no_material!.push(e.expressID);
      if (!e.quantities && !e.derived) issues.no_quantity!.push(e.expressID);
      if (!e.storey) issues.no_storey!.push(e.expressID);
      if (isType(e, 'IfcDoor') && prop(e, 'Pset_DoorCommon', 'FireRating') == null)
        issues.door_without_fire_rating!.push(e.expressID);
      if (isType(e, 'IfcWall') && prop(e, 'Pset_WallCommon', 'LoadBearing') == null)
        issues.wall_without_loadbearing_flag!.push(e.expressID);
      let n = 0;
      for (const [set, props] of Object.entries(e.psets ?? {}))
        for (const k of Object.keys(props))
          if (props[k] != null && prop(e, set, k) == null && String(props[k]) !== '') n++;
      if (n) {
        placeholders += n;
        issues.placeholder_values!.push(e.expressID);
      }
    }
    const summary = Object.fromEntries(Object.entries(issues).map(([k, ids]) => [k, ids.length]));
    return {
      elements: pool.length,
      issues: summary,
      placeholder_values_total: placeholders,
      ids_by_issue: Object.fromEntries(
        Object.entries(issues).map(([k, ids]) => [k, ids.slice(0, 400)]),
      ),
      ids: issues.no_material!.slice(0, 400),
    };
  };
